import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { PopulationSegment } from "@models/populationSegment.model";
import { IngestRun } from "@models/IngestRun.model";
import { Launch } from "@models/launch.model";
import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService";
import { IngestFileService } from "@services/ingest/ingestFileService";
import { EngagementService } from "@services/engagements/engagementService";
import useOrganizationSegments from "src/modules/organizations/segments/hooks/useOrganizationSegments";
import SegmentFormModal, {
  SegmentFormValues,
} from "src/modules/organizations/segments/components/SegmentFormModal";
import ModalDelete from "src/components/common/ModalDelete";
import Loader from "src/components/common/loader/loader";
import type { EngagementSegmentLinks } from "@services/engagements/commands/cmdGetSegmentLinks";
import { engagementStore } from "../../engagementStore";
import { workflowEvents } from "../../workflow/workflowEvents";
import { useItemRoute } from "../../workflow/useItemRoute";
import PopulationDetail from "./PopulationDetail";
import LinkIssuesBanner from "./LinkIssuesBanner";
import { IngestDefinitionRow } from "./types";
import { orderSpecs } from "./specOrder";
import "./dataFile.scss";

const definitionService = new IngestDefinitionService();
const fileService = new IngestFileService();

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/*
    Data File: the client organization's populations, each owning a data spec, a
    field map, and its uploaded files. Populations are org-level assets (name is
    unique per org, and both launches and ingest specs point at them), so this
    reads the same segments the organization page manages, through the same store.
*/
const DataFile = () => {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const engagementId = selectedEngagement?.id;
  const clientOrgId = selectedEngagement?.client?.id ?? 0;
  const clientName = selectedEngagement?.client?.name || "this client";

  const {
    segments,
    isLoading,
    isSaving,
    error,
    createSegment,
    updateSegment,
    deleteSegment,
    reloadSegments,
  } = useOrganizationSegments(clientOrgId);

  const [definitions, setDefinitions] = useState<IngestDefinitionRow[]>([]);
  const [runs, setRuns] = useState<IngestRun[]>([]);
  const [launches, setLaunches] = useState<Launch[]>([]);
  const [segmentLinks, setSegmentLinks] = useState<EngagementSegmentLinks | null>(null);

  const [ingestError, setIngestError] = useState<string | null>(null);

  const [selectedSegmentId, setSelectedSegmentId] = useItemRoute();
  /*
      Which spec the detail view is working in, when the population has several
      (MPN-1693). Held here rather than in PopulationDetail because a tab click
      navigates, and only this level survives that without losing the choice.
      Null means "the first" — reset whenever the population changes.
  */
  const [selectedSpecId, setSelectedSpecId] = useState<number | null>(null);
  useEffect(() => setSelectedSpecId(null), [selectedSegmentId]);
  const [formModal, setFormModal] = useState<{ segment: PopulationSegment | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PopulationSegment | null>(null);

  /*
      Specs and files are loaded independently and their failures reported: a
      failed load would otherwise render as "No data spec", which reads exactly
      like a population that genuinely has none.
  */
  const loadIngest = useCallback(async () => {
    if (!clientOrgId) return;
    const [defs, files] = await Promise.allSettled([
      definitionService.fetchDefinitions(clientOrgId),
      fileService.listFiles(clientOrgId),
    ]);
    if (defs.status === "fulfilled") setDefinitions(defs.value as IngestDefinitionRow[]);
    if (files.status === "fulfilled") setRuns(files.value);

    const failed = [
      defs.status === "rejected" && "data specs",
      files.status === "rejected" && "uploaded files",
    ].filter(Boolean);
    setIngestError(failed.length ? `Could not load ${failed.join(" or ")} for this client.` : null);
  }, [clientOrgId]);

  useEffect(() => {
    void loadIngest();
  }, [loadIngest]);

  const loadLaunches = useCallback(() => {
    if (!engagementId) return;
    new EngagementService()
      .getEngagementLaunches(engagementId)
      .then(setLaunches)
      .catch(() => setLaunches([]));
  }, [engagementId]);

  useEffect(() => {
    loadLaunches();
  }, [loadLaunches]);

  /*
      The segment-links read powers the orphan banner, the card health notes,
      and the detail's Links tab. Its failure is silent (null) — the page still
      works from the per-list loads; only the health layer goes quiet.
  */
  const loadLinks = useCallback(() => {
    if (!engagementId) return;
    new EngagementService()
      .getSegmentLinks(engagementId)
      .then(setSegmentLinks)
      .catch(() => setSegmentLinks(null));
  }, [engagementId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const touched = () => workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");

  const reloadIngest = () => {
    void loadIngest();
    loadLinks();
    touched();
  };

  // A link was written from the banner or the Links tab: every derived view
  // (cards, orphans, launch counts — including the segments' launch_count
  // that the shared-population warning reads) may have changed.
  const reloadLinksData = () => {
    void loadIngest();
    loadLaunches();
    loadLinks();
    void reloadSegments();
    touched();
  };

  /*
      A spec points at its population by FK; the legacy name string is what older
      configs carry, so fall back to it when the FK was never linked.
  */
  const definitionsFor = (segment: PopulationSegment) => {
    const linked = definitions.filter((d) => d.population_segment?.id === segment.id);
    if (linked.length) return orderSpecs(linked);
    // Legacy specs were never FK'd; match them on the name they carry instead.
    return orderSpecs(
      definitions.filter(
        (d) =>
          !d.population_segment &&
          (d.population_segment_name ?? "").trim().toLowerCase() ===
            segment.name.trim().toLowerCase(),
      ),
    );
  };

  const runsFor = (specs: readonly IngestDefinitionRow[]) => {
    const ids = new Set(specs.map((d) => d.id));
    return runs.filter((r) => r.ingest_definition && ids.has(r.ingest_definition.id));
  };

  const launchCountFor = (segment: PopulationSegment) =>
    launches.filter((l) => l.population_segment_id === segment.id).length;

  // Runs whose definition FK was never stamped — a validate that never succeeded.
  const unassignedRuns = useMemo(() => runs.filter((r) => !r.ingest_definition), [runs]);

  const selectedSegment = segments.find((s) => s.id === selectedSegmentId) ?? null;

  const handleSaveSegment = async (values: SegmentFormValues) => {
    const editing = formModal?.segment;
    if (editing) {
      await updateSegment(editing.id, values);
    } else {
      await createSegment(values);
    }
    setFormModal(null);
    touched();
  };

  const handleDeleteSegment = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSegment(deleteTarget.id);
      if (selectedSegmentId === deleteTarget.id) setSelectedSegmentId(null);
      touched();
    } finally {
      setDeleteTarget(null);
    }
  };

  const otherNames = (editing: PopulationSegment | null) =>
    segments.filter((s) => s.id !== editing?.id).map((s) => s.name.trim().toLowerCase());

  if (isLoading && segments.length === 0) {
    return <Loader />;
  }

  const modals = (
    <>
      {formModal && (
        <SegmentFormModal
          show
          segment={formModal.segment}
          existingNames={otherNames(formModal.segment)}
          isSaving={isSaving}
          onClose={() => setFormModal(null)}
          onSave={handleSaveSegment}
        />
      )}

      <ModalDelete
        title="Population"
        objectLabel={deleteTarget?.name ?? ""}
        deleteFn={handleDeleteSegment}
        visible={deleteTarget !== null}
        toggleVisibility={(visible: boolean) => {
          if (!visible) setDeleteTarget(null);
        }}
        isLoading={isSaving}
      />
    </>
  );

  if (selectedSegment) {
    const specs = definitionsFor(selectedSegment as PopulationSegment);
    const definition = specs.find((d) => d.id === selectedSpecId) ?? specs[0] ?? null;
    return (
      <>
        <PopulationDetail
          segment={selectedSegment as PopulationSegment}
          definition={definition}
          definitions={specs}
          onSelectDefinition={setSelectedSpecId}
          organizationId={clientOrgId}
          runs={runsFor(definition ? [definition] : [])}
          links={segmentLinks?.segments.find((s) => s.id === selectedSegment.id) ?? null}
          allLinks={segmentLinks}
          onBack={() => setSelectedSegmentId(null)}
          onRename={() => setFormModal({ segment: selectedSegment as PopulationSegment })}
          onDelete={() => setDeleteTarget(selectedSegment as PopulationSegment)}
          onSpecChanged={reloadIngest}
          onFilesChanged={reloadIngest}
          onLinksChanged={reloadLinksData}
        />
        {modals}
      </>
    );
  }

  if (segments.length === 0) {
    return (
      <>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        <div className="data-file__empty">
          <div className="data-file__empty-title">No populations yet for {clientName}</div>
          <div className="data-file__empty-copy">
            A population is the named list this engagement targets. Each one gets its own
            data spec, field map, and uploaded files.
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => setFormModal({ segment: null })}
            disabled={isSaving}
            style={{ fontSize: "0.72rem" }}
          >
            Create population
          </button>
        </div>
        {modals}
      </>
    );
  }

  return (
    <>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {ingestError && <div className="alert alert-warning py-2 small">{ingestError}</div>}

      {segmentLinks && (
        <LinkIssuesBanner
          orphans={segmentLinks.orphans}
          segments={segments as PopulationSegment[]}
          onChanged={reloadLinksData}
        />
      )}

      <div className="data-file__section">
        <div className="data-file__head">
          <i className="bi bi-people" style={{ color: "#64748b" }} />
          <span className="data-file__title">Populations</span>
          <span className="data-file__count">{segments.length}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary ms-auto"
            onClick={() => setFormModal({ segment: null })}
            disabled={isSaving}
            style={{ fontSize: "0.72rem" }}
          >
            New population
          </button>
        </div>
        <div className="data-file__body">
          <div className="data-file__grid">
            {segments.map((segment) => {
              const specs = definitionsFor(segment as PopulationSegment);
              const segmentRuns = runsFor(specs);
              const lastCompleted = segmentRuns.find((r) => r.status === "completed");
              const launchCount = launchCountFor(segment as PopulationSegment);

              const dotColor = !specs.length ? "#cbd5e1" : lastCompleted ? "#10b981" : "#f59e0b";
              const statusLabel = !specs.length
                ? "No data spec"
                : lastCompleted
                  ? "Receiving data"
                  : "Spec ready, no files yet";

              /*
                  Billing health from the links read. Only the gap is shown —
                  a green "billing ok" chip on every healthy card is noise.
                  journey_paths === null means billing is hidden from this
                  user (no Contracts view), which is silence, not a warning.
              */
              const links = segmentLinks?.segments.find((s) => s.id === segment.id);
              const billingGap = links?.journey_paths != null && links.journey_paths.length === 0;

              return (
                <div key={segment.id} className="population-card">
                  <div className="population-card__name">{segment.name}</div>
                  <div className="population-card__meta">
                    <div>
                      <span className="population-card__dot" style={{ background: dotColor }} />
                      {statusLabel}
                    </div>
                    {lastCompleted && (
                      <div className="population-card__muted">
                        Last file {formatDate(lastCompleted.create_date)}
                        {lastCompleted.row_count != null &&
                          ` · ${lastCompleted.row_count.toLocaleString()} rows`}
                      </div>
                    )}
                    <div className="population-card__muted">
                      {launchCount === 1 ? "1 launch" : `${launchCount} launches`} in this engagement
                    </div>
                    {specs.length > 1 && (
                      <div className="population-card__muted">{specs.length} data specs</div>
                    )}
                    {billingGap && (
                      <div style={{ color: "#b45309" }}>
                        <i className="bi bi-exclamation-triangle-fill me-1" />
                        No pricing group bills this population
                      </div>
                    )}
                  </div>
                  <div className="population-card__foot">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setSelectedSegmentId(segment.id)}
                      style={{ fontSize: "0.72rem" }}
                    >
                      {specs.length ? "Open" : "Set up"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {unassignedRuns.length > 0 && (
            <div className="data-file__note mt-3">
              <i className="bi bi-exclamation-circle me-1" />
              {unassignedRuns.length} uploaded {unassignedRuns.length === 1 ? "file is" : "files are"} not
              attached to a population — they were uploaded but never validated against a spec.
            </div>
          )}
        </div>
      </div>

      {modals}
    </>
  );
};

export default DataFile;
